import { DatePicker, Form, Input, InputNumber, Select, Tag } from "antd";
import dayjs from "dayjs";
import { BankAccountGroupedLookup } from "../../shared/lookup/BankAccountGroupedLookup";
import { SmartLookupField } from "../../shared/lookup/SmartLookupField";
import { UI_DATE_FORMAT } from "../../shared/utils/date";

export function VoucherHeaderForm({
  form,
  title,
  voucherType,
  isInvoice,
  isBankVoucher,
  setDirty,
  partyLabel,
  bankAccountLabel,
  partyBalanceText,
  bankBalanceText,
  onPartyChange,
  onBankAccountChange
}: {
  form: ReturnType<typeof Form.useForm>[0];
  title: string;
  voucherType: string;
  isInvoice: boolean;
  isBankVoucher: boolean;
  setDirty: (dirty: boolean) => void;
  partyLabel?: string;
  bankAccountLabel?: string;
  partyBalanceText?: string;
  bankBalanceText?: string;
  onPartyChange?: (value: { id: string | number | null; label: string; raw?: unknown }) => void;
  onBankAccountChange?: (value: { id: string | number | null; label: string; raw?: unknown }) => void;
}) {
  const isJournal = voucherType === "JournalEntry";
  const accountLabel = voucherType === "PaymentVoucher"
    ? "Account (Cash/Bank to pay from)"
    : "Account (Cash/Bank to receive into)";
  const partyLabelText = isJournal
    ? "Ledger Account"
    : voucherType === "PaymentVoucher"
      ? "Party (paid to)"
      : voucherType === "ReceiptVoucher"
        ? "Party (received from)"
        : "Party A/c name";
  const referenceLabel = voucherType === "PurchaseInvoice"
    ? "Supplier Invoice No."
    : voucherType === "SalesInvoice"
      ? "Customer Ref No."
      : "Reference No.";

  return (
    <Form form={form} layout="vertical" initialValues={{ billDate: dayjs(), adjustmentMethod: isInvoice ? "NewRef" : "OnAccount" }} onValuesChange={() => setDirty(true)}>
      <div className="voucher-header-shell">
        <div className="voucher-header-top">
          <Form.Item label="Voucher Type"><Tag>{title}</Tag></Form.Item>
          <Form.Item name="billNo" label={isInvoice ? "Bill No" : "Voucher No"}><Input placeholder="Auto when blank" /></Form.Item>
          <Form.Item name="billDate" label="Date" rules={[{ required: true }]}><DatePicker format={UI_DATE_FORMAT} style={{ width: "100%" }} /></Form.Item>
        </div>

        <div className={`voucher-header-row${isInvoice ? "" : " is-single"}`}>
          <Form.Item name="referenceNo" label={referenceLabel}><Input /></Form.Item>
          {isInvoice ? <Form.Item name="dueDate" label="Due Date"><DatePicker format={UI_DATE_FORMAT} style={{ width: "100%" }} /></Form.Item> : null}
        </div>

        {isBankVoucher ? (
          <>
            <div className="voucher-header-row is-single">
              <Form.Item name="bankAccountId" label={accountLabel} rules={[{ required: true, message: "Bank account is required" }]}>
                <BankAccountGroupedLookup
                  accountLabel={bankAccountLabel}
                  onSelect={(value) => {
                    form.setFieldValue("bankId", value.bankId);
                    form.setFieldValue("bankAccountId", value.bankAccountId);
                    onBankAccountChange?.({ id: value.bankAccountId, label: value.bankAccountLabel, raw: value });
                  }}
                />
              </Form.Item>
              <Form.Item name="bankId" hidden><Input /></Form.Item>
            </div>
            <div className="voucher-header-balance-row">
              <div className="voucher-balance-label">Current balance</div>
              <div className="voucher-balance-value"><Input readOnly value={bankBalanceText} /></div>
            </div>
          </>
        ) : null}

        <div className="voucher-header-row is-single">
          <Form.Item name="partyId" label={partyLabelText} rules={isInvoice ? [{ required: true, message: "Party is required" }] : []}>
            <SmartLookupField lookupKey="party" label={partyLabel} onChange={(v) => {
              form.setFieldValue("partyId", v.id);
              onPartyChange?.(v);
            }} />
          </Form.Item>
        </div>
        <div className="voucher-header-balance-row">
          <div className="voucher-balance-label">Current balance</div>
          <div className="voucher-balance-value"><Input readOnly value={partyBalanceText} /></div>
        </div>

        {isInvoice ? (
          <>
            <div className="voucher-header-row is-single">
              <Form.Item name="ledgerAccountId" label={voucherType === "PurchaseInvoice" ? "Purchase ledger" : "Sales ledger"}>
                <SmartLookupField lookupKey="accountLedger" onChange={(v) => form.setFieldValue("ledgerAccountId", v.id)} />
              </Form.Item>
            </div>
          </>
        ) : null}

        {!isJournal ? (
          <div className="voucher-header-row">
            <Form.Item name="adjustmentMethod" label="Adjustment">
              <Select options={[
                { value: "NewRef", label: "New Ref" },
                { value: "AgainstRef", label: "Against Ref" },
                { value: "Advance", label: "Advance" },
                { value: "OnAccount", label: "On Account" }
              ]} />
            </Form.Item>
            {!isInvoice ? <Form.Item name="amount" label="Amount" rules={[{ required: true }]}><InputNumber min={0} style={{ width: "100%" }} /></Form.Item> : null}
          </div>
        ) : null}

        <div className="voucher-header-row is-single">
          <Form.Item name="narration" label="Narration"><Input /></Form.Item>
        </div>
      </div>
    </Form>
  );
}
